/**
 * Ajout AGORA (absent du bundle de référence) – diaporama de photos en fond (écran de connexion, 04/11/2026) : de
 * vraies photos d'installations PRODEVAL à la place des illustrations, qui passent de l'une à l'autre par un fondu
 * enchaîné lent pendant qu'un zoom très léger les anime (effet Ken Burns). Décor seulement : aucun texte sur les
 * photos, le contenu (carte d'actualité, puce) est posé par-dessus par l'appelant.
 *
 *  - Images responsives : AVIF puis WebP (`srcset` par largeur), JPEG de repli ; le navigateur choisit le format et la
 *    résolution (`sizes`). Les photos ne bloquent rien : chacune apparaît en fondu une fois chargée, sur la teinte de
 *    la marque ; la suivante n'est montrée que si elle est chargée.
 *  - Accessibilité : photos décoratives (`alt` vide, `aria-hidden`). Le mouvement dure plus de 5 secondes, d'où le
 *    bouton « Mettre en pause l'animation » (WCAG 2.2.2), libellé traduit fourni par l'appelant ; en mode contrôlé
 *    (`paused` + `onPausedChange`), l'appelant peut aussi arrêter ce qui défile à côté (actualités de la connexion).
 *    Mouvement réduit : première photo fixe, ni zoom ni fondu.
 *
 * Styles et mouvement (5 informations, voile aux couleurs AGORA, mode sombre) : ./diaporama.css.
 */
import React from 'react';
import { cx, h, Icon } from './base';
import { DUREES, mouvementReduit } from './mouvement';
import './diaporama.css';

export interface SlidePhoto {
  /** Sources AVIF et WebP au format `srcset` (« url 800w, url 1280w ») : le navigateur prend le premier format qu'il lit. */
  avif?: string;
  webp?: string;
  /** Repli JPEG (navigateur sans AVIF ni WebP). */
  jpeg: string;
  /**
   * Cadrage (`object-position`, point fixe du zoom) : garde le sujet dans le cadre quel que soit le format de l'écran,
   * ex. `30% 55%`. Défaut : centre.
   */
  focus?: string;
}

export interface PhotoSlideshowProps {
  photos: SlidePhoto[];
  /** Largeur affichée (attribut `sizes`), pour que le navigateur télécharge la bonne résolution. */
  sizes: string;
  /** Libellés traduits du bouton (« Mettre en pause l'animation », « Reprendre l'animation »). */
  pauseLabel: string;
  playLabel: string;
  /** Pause contrôlée par l'appelant (avec `onPausedChange`) ; sinon le diaporama la gère seul. */
  paused?: boolean;
  onPausedChange?: (paused: boolean) => void;
  className?: string;
}

/** Temps d'affichage d'une photo : 40 × la durée longue (12,8 s). */
const AFFICHAGE = DUREES.long * 40;

export function PhotoSlideshow(p: PhotoSlideshowProps) {
  const [pauseInterne, setPauseInterne] = React.useState(false);
  const pause = p.onPausedChange ? !!p.paused : pauseInterne;
  const [{ actif, precedent }, setVue] = React.useState<{ actif: number; precedent: number | null }>({ actif: 0, precedent: null });
  const [prets, setPrets] = React.useState<readonly number[]>([]);
  const pretsRef = React.useRef(prets);
  pretsRef.current = prets;
  const n = p.photos.length;

  React.useEffect(() => {
    if (pause || n < 2) return undefined;
    const id = window.setInterval(() => {
      if (mouvementReduit()) return;
      // Photo suivante seulement si elle est chargée : sinon la photo affichée reste, sans fondu vers le vide.
      setVue((v) => (pretsRef.current.includes((v.actif + 1) % n) ? { actif: (v.actif + 1) % n, precedent: v.actif } : v));
    }, AFFICHAGE);
    return () => window.clearInterval(id);
  }, [pause, n]);

  // La photo précédente reste opaque sous la nouvelle pendant le fondu (pas de voile clair à mi-fondu) et se cache à la
  // fin de l'apparition de la nouvelle – même si l'onglet, passé en arrière-plan, a suspendu l'animation entre-temps.
  const finApparition = (i: number) => (e: React.AnimationEvent) => {
    if (e.target === e.currentTarget) setVue((v) => (v.actif === i ? { ...v, precedent: null } : v));
  };

  const basculer = () => {
    if (p.onPausedChange) p.onPausedChange(!pause);
    else setPauseInterne(!pause);
  };
  const libelle = pause ? p.playLabel : p.pauseLabel;

  return h('div', { className: cx('agx-diaporama', pause && 'agx-diaporama-pause', p.className) },
    h('div', { className: 'agx-diaporama-photos', 'aria-hidden': true },
      p.photos.map((photo, i) => h('picture', {
        key: i,
        className: cx('agx-diaporama-photo', i === actif && 'agx-diaporama-on', i === precedent && 'agx-diaporama-avant', prets.includes(i) && 'agx-diaporama-pret'),
        style: photo.focus ? ({ '--agx-diaporama-cadrage': photo.focus } as React.CSSProperties) : undefined,
        onAnimationEnd: finApparition(i),
      },
      photo.avif ? h('source', { type: 'image/avif', srcSet: photo.avif, sizes: p.sizes }) : null,
      photo.webp ? h('source', { type: 'image/webp', srcSet: photo.webp, sizes: p.sizes }) : null,
      // Les photos suivantes passent après le reste de la page (fetchpriority en minuscules : React 18 le transmet tel quel).
      h('img', {
        src: photo.jpeg, alt: '', decoding: 'async', draggable: false, fetchpriority: i === 0 ? undefined : 'low',
        onLoad: () => setPrets((l) => (l.includes(i) ? l : [...l, i])),
      })))),
    n > 1 ? h('button', {
      type: 'button', className: 'ag-btn ag-btn-glass ag-glass agx-diaporama-bouton', 'aria-label': libelle, title: libelle, onClick: basculer,
    }, h(Icon, { name: pause ? 'lecture' : 'pause', size: 16 })) : null);
}
